<template>
  <AppLayout>
    <div class="max-w-7xl mx-auto space-y-6">
      <!-- Hero -->
      <section class="move-card p-6 md:p-8 overflow-hidden relative">
        <div
          class="absolute inset-0 opacity-40 pointer-events-none"
          style="background: radial-gradient(circle at top right, var(--move-accent-muted), transparent 55%)"
        />
        <div class="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div class="text-left">
            <p class="text-xs font-bold tracking-widest uppercase move-text-faint mb-2">Personal</p>
            <h2 class="text-2xl md:text-3xl font-extrabold move-text mb-2">My tasks</h2>
            <p class="move-text-muted max-w-xl">
              Tasks assigned to you or created by you across all boards.
            </p>
          </div>
          <p v-if="!loading" class="text-sm move-text-faint shrink-0">
            {{ filteredTasks.length }} {{ filteredTasks.length === 1 ? 'task' : 'tasks' }}
            <template v-if="!showArchived && archivedTaskCount">
              · {{ archivedTaskCount }} archived
            </template>
          </p>
        </div>
      </section>

      <!-- Toolbar -->
      <section class="move-board-toolbar flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="text-xs font-bold tracking-widest uppercase move-text-faint">View</span>
          <div class="move-view-toggle" role="group" aria-label="Task view mode">
            <button
              type="button"
              class="move-view-toggle__btn"
              :class="{ 'is-active': viewMode === 'list' }"
              @click="setViewMode('list')"
            >
              <span class="material-icons text-base">view_list</span>
              List
            </button>
            <button
              type="button"
              class="move-view-toggle__btn"
              :class="{ 'is-active': viewMode === 'columns' }"
              @click="setViewMode('columns')"
            >
              <span class="material-icons text-base">view_column</span>
              By board
            </button>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
          <div class="move-field !mb-0">
            <label for="boardFilter" class="!text-xs !mb-1 move-text-faint uppercase tracking-wider">Board</label>
            <MoveSelect
              id="boardFilter"
              v-model="filterBoard"
              :options="boardOptions"
              size="compact"
              class="move-toolbar-select"
            />
          </div>
          <div class="move-field !mb-0">
            <label for="sortBy" class="!text-xs !mb-1 move-text-faint uppercase tracking-wider">Sort by</label>
            <MoveSelect
              id="sortBy"
              v-model="sortBy"
              :options="sortOptions"
              size="compact"
              class="move-toolbar-select"
            />
          </div>
          <div class="move-field !mb-0">
            <label class="!text-xs !mb-1 move-text-faint uppercase tracking-wider">Status</label>
            <div class="move-view-toggle" role="group" aria-label="Task archive filter">
              <button
                type="button"
                class="move-view-toggle__btn"
                :class="{ 'is-active': !showArchived }"
                @click="showArchived = false"
              >
                Active
              </button>
              <button
                type="button"
                class="move-view-toggle__btn"
                :class="{ 'is-active': showArchived }"
                @click="showArchived = true"
              >
                Archived
              </button>
            </div>
          </div>
        </div>
      </section>

      <div v-if="loading" class="flex justify-center py-16">
        <div class="move-spinner" aria-label="Loading tasks" />
      </div>

      <div v-else-if="error" class="move-alert-error">
        {{ error }}
      </div>

      <div v-else-if="filteredTasks.length === 0" class="move-card move-empty-state py-16">
        <span class="material-icons">task_alt</span>
        <p class="font-semibold move-text text-lg">No tasks found</p>
        <p class="text-sm move-text-faint mt-1">
          {{ filterBoard === 'all'
            ? (showArchived ? 'No archived tasks yet.' : 'Create a task on a board to get started.')
            : (showArchived ? 'No archived tasks on this board.' : 'No tasks on this board match your filters.')
          }}
        </p>
      </div>

      <!-- List view -->
      <div v-else-if="viewMode === 'list'" class="space-y-3">
        <article
          v-for="task in sortedTasks"
          :key="task.id"
          class="move-task-card"
          :class="{ 'move-task-card--archived': task.archived }"
        >
          <div class="flex items-start justify-between gap-3">
            <button
              type="button"
              class="move-task-card__open"
              @click="viewTask(task)"
            >
              <div class="flex flex-wrap items-center gap-2 mb-2">
                <h3 class="text-base font-bold move-text truncate">{{ task.title }}</h3>
                <span class="move-priority" :class="priorityClass(task.priority)">
                  {{ task.priority || 'medium' }}
                </span>
              </div>
              <p v-if="task.description" class="text-sm move-text-muted line-clamp-2 mb-3">
                {{ task.description }}
              </p>
              <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm move-text-faint">
                <span class="inline-flex items-center gap-1">
                  <span class="material-icons text-base">event</span>
                  {{ formatDueDate(task.dueDate) }}
                </span>
                <span class="inline-flex items-center gap-1">
                  <span class="material-icons text-base">folder</span>
                  {{ getBoardName(task.boardId) }}
                </span>
                <span class="inline-flex items-center gap-1">
                  <span class="material-icons text-base">schedule</span>
                  Created {{ formatCreatedDate(task.createdAt) }}
                </span>
              </div>
            </button>
            <button
              type="button"
              class="move-icon-btn shrink-0"
              :aria-label="task.archived ? 'Unarchive task' : 'Archive task'"
              :title="task.archived ? 'Unarchive' : 'Archive'"
              :disabled="archivingTaskId === task.id"
              @click="toggleTaskArchived(task)"
            >
              <span class="material-icons">{{ task.archived ? 'unarchive' : 'archive' }}</span>
            </button>
          </div>
        </article>
      </div>

      <!-- Columns by board -->
      <div
        v-else
        class="move-kanban-scroll flex gap-4 overflow-x-auto pb-2 -mx-1 px-1"
      >
        <section
          v-for="group in tasksByBoard"
          :key="group.boardId"
          class="move-my-tasks-column"
        >
          <header class="move-my-tasks-column__header">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <h3 class="font-bold move-text truncate">{{ group.boardName }}</h3>
                <p class="text-xs move-text-faint mt-0.5">{{ group.tasks.length }} tasks</p>
              </div>
              <router-link
                :to="`/board/${group.boardId}`"
                class="move-kanban-meta shrink-0 hover:opacity-80"
              >
                Open
              </router-link>
            </div>
          </header>
          <div class="move-my-tasks-column__body">
            <article
              v-for="task in group.tasks"
              :key="task.id"
              class="move-kanban-task w-full text-left p-3"
              :class="{ 'move-kanban-task--archived': task.archived }"
              @click="viewTask(task)"
            >
              <div class="flex items-start justify-between gap-2 mb-1.5">
                <p class="font-semibold move-text text-sm leading-snug min-w-0 flex-1">{{ task.title }}</p>
                <span class="move-priority shrink-0" :class="priorityClass(task.priority)">
                  {{ task.priority || 'medium' }}
                </span>
                <button
                  type="button"
                  class="move-kanban-task__archive !ml-0"
                  :aria-label="task.archived ? 'Unarchive task' : 'Archive task'"
                  :title="task.archived ? 'Unarchive' : 'Archive'"
                  :disabled="archivingTaskId === task.id"
                  @click.stop="toggleTaskArchived(task)"
                >
                  <span class="material-icons text-base">{{ task.archived ? 'unarchive' : 'archive' }}</span>
                </button>
              </div>
              <p v-if="task.description" class="text-xs move-text-muted line-clamp-2 mb-2">
                {{ task.description }}
              </p>
              <div class="flex flex-col gap-1 text-xs move-text-faint">
                <span class="inline-flex items-center gap-1">
                  <span class="material-icons text-sm">event</span>
                  {{ formatDueDate(task.dueDate) }}
                </span>
                <span class="inline-flex items-center gap-1">
                  <span class="material-icons text-sm">schedule</span>
                  {{ formatCreatedDate(task.createdAt) }}
                </span>
              </div>
            </article>
          </div>
        </section>
      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { moveApi } from '../services/moveApi'
import { mapTaskFromApi } from '../utils/moveData'
import AppLayout from '../layouts/AppLayout.vue'

const VIEW_KEY = 'move-my-tasks-view'
const SORT_KEY = 'move-my-tasks-sort'
const BOARD_FILTER_KEY = 'move-my-tasks-board'

const router = useRouter()
const tasks = ref([])
const boards = ref([])
const loading = ref(true)
const error = ref('')
const filterBoard = ref(localStorage.getItem(BOARD_FILTER_KEY) || 'all')
const sortBy = ref(localStorage.getItem(SORT_KEY) || 'dueDate')
const viewMode = ref(localStorage.getItem(VIEW_KEY) || 'list')
const showArchived = ref(false)
const archivingTaskId = ref(null)

const sortOptions = [
  { value: 'dueDate', label: 'Due date' },
  { value: 'createdAt', label: 'Date created' },
  { value: 'priority', label: 'Priority' }
]

const boardOptions = computed(() => [
  { value: 'all', label: 'All boards' },
  ...boards.value.map((board) => ({
    value: String(board.id),
    label: board.name
  }))
])

const archivedTaskCount = computed(() => tasks.value.filter((task) => task.archived).length)

const filteredTasks = computed(() => {
  const byArchive = tasks.value.filter((task) => (showArchived.value ? task.archived : !task.archived))
  if (filterBoard.value === 'all') {
    return byArchive
  }
  return byArchive.filter((task) => String(task.boardId) === String(filterBoard.value))
})

const sortTasks = (list) => {
  const sorted = [...list]

  sorted.sort((a, b) => {
    if (sortBy.value === 'dueDate') {
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER
      return dateA - dateB
    }

    if (sortBy.value === 'createdAt') {
      const dateA = new Date(a.createdAt || 0).getTime()
      const dateB = new Date(b.createdAt || 0).getTime()
      return dateB - dateA
    }

    if (sortBy.value === 'priority') {
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1)
    }

    return 0
  })

  return sorted
}

const sortedTasks = computed(() => sortTasks(filteredTasks.value))

const tasksByBoard = computed(() => {
  const grouped = new Map()

  for (const task of sortedTasks.value) {
    const boardId = String(task.boardId)
    if (!grouped.has(boardId)) {
      grouped.set(boardId, {
        boardId: task.boardId,
        boardName: task.boardName || getBoardName(task.boardId),
        tasks: []
      })
    }
    grouped.get(boardId).tasks.push(task)
  }

  const orderedBoards = boards.value
    .filter((board) => grouped.has(String(board.id)))
    .map((board) => grouped.get(String(board.id)))

  for (const [boardId, group] of grouped.entries()) {
    if (!boards.value.some((board) => String(board.id) === boardId)) {
      orderedBoards.push(group)
    }
  }

  return orderedBoards
})

watch(viewMode, (value) => {
  localStorage.setItem(VIEW_KEY, value)
})

watch(sortBy, (value) => {
  localStorage.setItem(SORT_KEY, value)
})

watch(filterBoard, (value) => {
  localStorage.setItem(BOARD_FILTER_KEY, value)
})

const setViewMode = (mode) => {
  viewMode.value = mode
}

const priorityClass = (priority) => {
  const key = (priority || 'medium').toLowerCase()
  return `move-priority--${key}`
}

const formatDueDate = (dueDate) => {
  if (!dueDate) return 'No due date'
  const date = new Date(dueDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(date)
  due.setHours(0, 0, 0, 0)
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Due today'
  if (diffDays === 1) return 'Due tomorrow'
  if (diffDays === -1) return 'Due yesterday'
  if (diffDays < 0) return `Overdue · ${date.toLocaleDateString()}`
  if (diffDays <= 7) return `Due in ${diffDays}d`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatCreatedDate = (createdAt) => {
  if (!createdAt) return 'Unknown'
  const date = new Date(createdAt)
  const diffMs = Date.now() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays <= 0) return 'today'
  if (diffDays === 1) return 'yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

onMounted(async () => {
  try {
    loading.value = true
    error.value = ''
    const [tasksResponse, boardsResponse] = await Promise.all([
      moveApi.myTasks({ includeArchived: true }),
      moveApi.listBoards()
    ])
    tasks.value = (tasksResponse.data.data || []).map(mapTaskFromApi)
    boards.value = boardsResponse.data.data || []
  } catch (err) {
    console.error('Error loading my tasks:', err)
    error.value = 'Could not load your tasks. Please try again.'
  } finally {
    loading.value = false
  }
})

const getBoardName = (boardId) => {
  const board = boards.value.find((b) => String(b.id) === String(boardId))
  return board?.name || tasks.value.find((t) => String(t.boardId) === String(boardId))?.boardName || 'Unknown board'
}

const viewTask = (task) => {
  router.push(`/board/${task.boardId}?task=${task.id}`)
}

const toggleTaskArchived = async (task) => {
  if (!task?.id || archivingTaskId.value) return

  const nextArchived = !task.archived
  archivingTaskId.value = task.id

  try {
    await moveApi.updateTask(task.boardId, task.id, { archived: nextArchived })
    tasks.value = tasks.value.map((item) => (
      String(item.id) === String(task.id)
        ? { ...item, archived: nextArchived, archivedAt: nextArchived ? new Date().toISOString() : null }
        : item
    ))
  } catch (err) {
    console.error('Error updating task archive state:', err)
  } finally {
    archivingTaskId.value = null
  }
}
</script>

<script>
export default {
  name: 'MyTasksView'
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.material-icons {
  font-family: 'Material Icons', sans-serif;
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  display: inline-block;
}
</style>
